import { defineStore } from 'pinia';
import { researchData, analyzeDRG } from 'src/services/ResourceService';
import _ from 'lodash';
import { ref } from 'vue';
import { useGrouperStore } from 'stores/grouper-store';
import { AnalyzeDrgRequestDto } from 'src/contracts/analyzeDrgRequestDto';
import { GetResearchResponse, MetadataResearchSection } from 'src/contracts/getMetadataResearchResponse';
import { MerckResult, searchMerck } from 'src/services/merckService';
import { isEmpty } from 'src/common/arrayUtil';
import { DrgAnalysis } from 'src/contracts/drgAnalysis';

export const useResourcesStore = defineStore('resourcesStore', () => {

    const analysisData = ref<DrgAnalysis>();
    const researchMetadata = ref<MetadataResearchSection[]>([]);


    async function getResearches() {
        const diagnosisCodes = useGrouperStore().diagnosisCodes;

        const codes = diagnosisCodes.map(diagnose => diagnose.code).map(code => code.replace('\.', ''))
        const promises = codes.map(async code => {
            let result;
            try {
                result = await researchData(code, 'ICD10CM_DX');
            } catch (e) {
                result = await researchData(code, 'ICD10CM_E');
            }
            return result;
        });

        const responses = await Promise.all(promises);
        const aggregatedData = aggregateResponses(responses);
        try {
            const merckData = await getMerck();
            if (!isEmpty(merckData)) {
                aggregatedData.push({
                    title: 'Merck Manual Professional',
                    articles: merckData.map(merck => {
                        return { title: merck.title, link: merck.link, is_best_match: true }
                    }),
                    best_count: merckData.length,
                    other_count: 0
                });
            }
        } catch (e) {
            console.error('Error fetching Merck data');
            console.error(e);
        }

        researchMetadata.value = aggregatedData
    }

    async function getResearch(code: string, codeType: string) {
        if (code && code.length > 2) {
            const response = await researchData(code, codeType);

            const research_sections: MetadataResearchSection[] = _.defaultTo(response?.research_sections.map((section) => ({
                ...section,
                best_count: section.best_count ?? 0,
                other_count: section.other_count ?? 0,
                articles: [...section?.articles ?? []],
                coding_advices: [...section?.coding_advices ?? []]
            })), []);


            try {
                const merckData = await getMerck();
                if (!isEmpty(merckData)) {
                    research_sections.push({
                        title: 'Merck Manual Professional',
                        articles: merckData.map(merck => {
                            return { title: merck.title, link: merck.link, is_best_match: true }
                        }),
                        best_count: merckData.length,
                        other_count: 0
                    });
                }
            } catch (e) {
                console.error('Error fetching Merck data');
                console.error(e);
            }

            researchMetadata.value = research_sections
        }
    }

    async function getMerck(): Promise<MerckResult[]> {
        const diagnosisCodes = useGrouperStore().diagnosisCodes;

        const codes = diagnosisCodes.map(diagnose => diagnose.code).map(code => code);
        const promises = codes.map(async code => {

            return await searchMerck(code);
        });

        const responses = await Promise.all(promises);

        const deDuped: MerckResult[] = [];
        responses.forEach((response) => {
            if (response?.title && response?.link && !deDuped.some(x => x.link == response.link)) {
                deDuped.push(response);
            }
        });

        return deDuped;
    }

    function aggregateResponses(responses: (GetResearchResponse | undefined)[]): MetadataResearchSection[] {

        const research_sections: MetadataResearchSection[] = [];

        function findResearchSection(researchSections: MetadataResearchSection[], type: string): MetadataResearchSection | undefined {
            return researchSections.find(section => section.research_type === type);
        }

        responses.forEach(response => {
            response?.research_sections.forEach(section => {
                const existingSection = findResearchSection(research_sections, section?.research_type || '');

                if (existingSection) {
                    existingSection.articles.push(...section?.articles ?? []);
                    existingSection.coding_advices?.push(...section?.coding_advices ?? [])
                    existingSection.best_count += section.best_count ?? 0;
                    existingSection.other_count += section.other_count ?? 0;
                } else {
                    research_sections.push({
                        ...section,
                        best_count: section.best_count ?? 0,
                        other_count: section.other_count ?? 0,
                        articles: [...section?.articles ?? []],
                        coding_advices: [...section?.coding_advices ?? []]
                    });
                }
            });
        });

        return research_sections;
    }

    async function getMsDrgAnalysis(): Promise<void> {
        const info = useGrouperStore().primaryDRG;
        const patientData = useGrouperStore().patient;

        const diagnosis = useGrouperStore().diagnosisCodes;
        const cc_present = diagnosis.some(diagnose => diagnose.drgModifier?.includes('CC'));

        const request: AnalyzeDrgRequestDto = {
            drg_analysis_input: {
                drg: info?.drg,
                grouper_version: grouperVersion(info?.grouper_version),
                coding_system: patientData?.codingSystem,
                diagnoses: {
                    diagnosis: diagnosis
                },
                procedures: {
                    procedure: useGrouperStore().procedureCodes
                },
                los: patientData?.los,
                cc_is_present: cc_present
            }
        }

        analysisData.value = await analyzeDRG(request);
    }

    function grouperVersion(version: string | undefined | null): string {
        return version?.slice(0, -1) + '.' + version?.slice(-1);
    }

    return {
        getResearches,
        getMsDrgAnalysis,
        getResearch,
        analysisData,
        aggregateResponses,
        researchMetadata
    }
})
